import { useEffect, useState } from 'react';
import { Globe, Plus, Trash2, CheckCircle, XCircle } from 'lucide-react';

interface DnsRecord {
  id: string;
  domain: string;
  type: string;
  name: string;
  value: string;
  ttl: number;
}

interface Domain {
  name: string;
  ttl: number;
}

export default function DNS() {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<{domain: string; verified: boolean; resolvedTo?: string} | null>(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/dns/domains').then((r) => r.json()),
      fetch('/api/dns/records').then((r) => r.json()),
    ])
      .then(([domainsData, recordsData]) => {
        setDomains(domainsData);
        setRecords(recordsData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const deleteRecord = async (id: string) => {
    if (!confirm('Are you sure you want to delete this DNS record?')) return;

    await fetch(`/api/dns/records/${id}`, { method: 'DELETE' });
    setRecords(records.filter((r) => r.id !== id));
  };

  const verifyDomain = async (domain: string) => {
    setVerifying(domain);
    const res = await fetch(`/api/dns/verify/${domain}`, { method: 'POST' });
    const result = await res.json();
    setVerifyResult(result);
    setVerifying(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">DNS Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg font-medium"
        >
          <Plus className="w-5 h-5" />
          Add Record
        </button>
      </div>

      {/* Managed Domains */}
      <div className="bg-gray-800 rounded-xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">
          DigitalOcean Managed Domains
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {domains.map((domain) => (
            <div
              key={domain.name}
              className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-gray-400" />
                <span className="text-white">{domain.name}</span>
              </div>
              <button
                onClick={() => verifyDomain(domain.name)}
                disabled={verifying === domain.name}
                className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-500 rounded text-white"
              >
                {verifying === domain.name ? 'Checking...' : 'Verify'}
              </button>
            </div>
          ))}
          {domains.length === 0 && (
            <p className="text-gray-500 col-span-full">
              No domains managed by DigitalOcean
            </p>
          )}
        </div>
      </div>

      {/* Verification Result */}
      {verifyResult && (
        <div className={`mb-8 p-4 rounded-lg ${verifyResult.verified ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
          <div className="flex items-center gap-3">
            {verifyResult.verified ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400" />
            )}
            <div>
              <p className={verifyResult.verified ? 'text-green-400' : 'text-red-400'}>
                {verifyResult.domain} - {verifyResult.verified ? 'Verified' : 'Not Verified'}
              </p>
              {verifyResult.resolvedTo && (
                <p className="text-sm text-gray-400">
                  Resolves to: {verifyResult.resolvedTo}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DNS Records */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">DNS Records</h2>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Domain
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Type
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Name
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Value
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                TTL
              </th>
              <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {records.map((record) => (
              <tr key={record.id} className="hover:bg-gray-700/50">
                <td className="px-6 py-4 text-white">{record.domain}</td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 bg-gray-700 rounded text-sm text-gray-300">
                    {record.type}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-300">{record.name}</td>
                <td className="px-6 py-4 text-gray-300 font-mono text-sm truncate max-w-xs">
                  {record.value}
                </td>
                <td className="px-6 py-4 text-gray-400">{record.ttl}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => deleteRecord(record.id)}
                    className="p-2 text-gray-400 hover:text-red-400 rounded-lg hover:bg-gray-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {records.length === 0 && (
          <div className="text-center py-12">
            <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              No DNS records
            </h3>
            <p className="text-gray-400">
              Add DNS records to manage your domains
            </p>
          </div>
        )}
      </div>

      {/* Create Record Modal */}
      {showCreateModal && (
        <CreateRecordModal
          domains={domains}
          onClose={() => setShowCreateModal(false)}
          onCreated={(record) => {
            setRecords([...records, record]);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}

interface CreateRecordModalProps {
  domains: Domain[];
  onClose: () => void;
  onCreated: (record: DnsRecord) => void;
}

function CreateRecordModal({ domains, onClose, onCreated }: CreateRecordModalProps) {
  const [form, setForm] = useState({
    domain: domains[0]?.name || '',
    type: 'CNAME',
    name: '',
    value: '',
    ttl: 3600,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/dns/records', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create record');
      }

      const record = await res.json();
      onCreated(record);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-semibold text-white mb-6">Add DNS Record</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Domain
            </label>
            <select
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {domains.map((d) => (
                <option key={d.name} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Type
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="A">A</option>
              <option value="CNAME">CNAME</option>
              <option value="TXT">TXT</option>
              <option value="MX">MX</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="@ or subdomain"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Value
            </label>
            <input
              type="text"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              placeholder="IP address or hostname"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          {error && <div className="text-red-400 text-sm">{error}</div>}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg font-medium"
            >
              {loading ? 'Creating...' : 'Create Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
